import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, Lock, ArrowLeft, User } from "lucide-react";
import { useAuth } from "@/config/auth";
import { TOSS_LOGO_URL, BRAND } from "@/lib/branding";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email || !password) { toast.error("All fields required"); return; }
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      const u = await register(email.trim(), password, name.trim());
      toast.success(`Welcome to ${BRAND.short}, ${u.name}`);
      navigate("/advanced", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F4F8F4] via-[#F8F9F8] to-[#E9F2EA] flex items-center justify-center p-6" data-testid="register-page">
      <div className="w-full max-w-md">
        <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-xs text-[#617364] hover:text-[#43A047] mb-6" data-testid="back-home">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to home
        </button>
        <Card className="border-[#E0E6E1] bg-white shadow-sm">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <img src={TOSS_LOGO_URL} alt={BRAND.short} className="h-10 w-auto object-contain" />
              <div>
                <h1 className="text-xl font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>{BRAND.short}</h1>
                <p className="text-[10px] text-[#617364] tracking-wider uppercase">{BRAND.from} · Create Account</p>
              </div>
            </div>

            <h2 className="text-2xl font-light text-[#1B291D] mb-1" style={{ fontFamily: 'Work Sans' }}>Create your account</h2>
            <p className="text-xs text-[#617364] mb-6">Save assessments, download reports and track sustainability over time.</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Full name</Label>
                <div className="relative mt-1">
                  <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#617364]" />
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" className="pl-9 border-[#E0E6E1]" data-testid="register-name" autoFocus />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Email</Label>
                <div className="relative mt-1">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#617364]" />
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="pl-9 border-[#E0E6E1]" data-testid="register-email" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Password</Label>
                <div className="relative mt-1">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#617364]" />
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="pl-9 border-[#E0E6E1]" data-testid="register-password" />
                </div>
              </div>

              <Button type="submit" disabled={busy} className="w-full bg-[#43A047] hover:bg-[#388E3C] text-white" data-testid="register-submit">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating account…</> : "Create account"}
              </Button>
            </form>

            <div className="mt-6 pt-5 border-t border-[#E0E6E1] text-center">
              <p className="text-xs text-[#617364]">
                Already have an account? <Link to="/login" className="text-[#43A047] font-medium hover:underline" data-testid="link-login">Sign in</Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
